import { Popover, type PopoverPosition } from '@mui/material';
import { useMap } from '@vis.gl/react-google-maps';
import { noop } from 'lodash';
import { type PropsWithChildren, useEffect, useState } from 'react';

type Props = {
  anchor: google.maps.marker.AdvancedMarkerElement;
  onClose?: () => void;
};

const MARGIN_THRESHOLD = 16;

/**
 * A Popover anchored to a marker on the map.
 */
export const MarkerPopover = ({
  children,
  anchor,
  onClose,
}: PropsWithChildren<Props>) => {
  const map = useMap();
  const [anchorPosition, setAnchorPosition] = useState<
    PopoverPosition | undefined
  >(undefined);

  useEffect(() => {
    if (!map) return noop;

    class AnchorPositionObserverOverlay extends google.maps.OverlayView {
      onAdd = noop;

      draw() {
        if (!anchor.position || !map) {
          setAnchorPosition(undefined);
          return;
        }

        const position = this.getProjection().fromLatLngToContainerPixel(
          anchor.position,
        );

        if (!position) {
          setAnchorPosition(undefined);
          return;
        }

        const { left: mapLeft, top: mapTop } = map
          .getDiv()
          .getBoundingClientRect();

        setAnchorPosition({
          left: position.x + mapLeft,
          top: position.y + mapTop,
        });
      }

      onRemove() {
        setAnchorPosition(undefined);
      }
    }

    const overlay = new AnchorPositionObserverOverlay();
    overlay.setMap(map);

    return () => {
      overlay.setMap(null);
    };
  }, [map, anchor]);

  const handleEntering = (element: HTMLElement) => {
    if (!anchorPosition || !map) return;

    const height = element.offsetHeight;
    const bottom = anchorPosition.top + height;
    const windowBottom = window.innerHeight - MARGIN_THRESHOLD;

    // Shift map if anchor will be overlapped by popover
    if (bottom > windowBottom) {
      map.panBy(0, bottom - windowBottom);
    }
  };

  return (
    <Popover
      open={!!anchorPosition}
      anchorReference="anchorPosition"
      anchorPosition={anchorPosition}
      onClose={onClose}
      transformOrigin={{
        vertical: 'top',
        horizontal: 'center',
      }}
      slotProps={{
        paper: {
          sx: {
            width: 260,
          },
        },
        transition: {
          onEntering: handleEntering,
        },
      }}
      sx={{ mt: 0.5 }}
      marginThreshold={MARGIN_THRESHOLD}
    >
      {children}
    </Popover>
  );
};
